import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CVANotificationService } from '../../../../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../../../../models/notification-class.enum';
import { catchError, combineLatest, filter, finalize, forkJoin, map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { AccEligibilityService } from '../../../../../services/acc-eligibility/acc-eligibility.service';
import { environment } from '../../../../../../environments/environment';
import { IAccEligibility, IAccEligibilityResult, IAccServiceCode, ISaveAccEligibility, ISaveAccEligibilityMin } from '../../../../../models/acc-elgibility.model';
import { ConsultationRecommendationService } from '../../../../../services/consultation-recommendation/consultation-recommendation.service';
import { PractitionerService } from '../../../../../services/practitioner/practitioner.service';
import { PractitionerResponse } from '../../../../../models/practitioner.interface';
import { CVA_CONSTANTS } from '../../../../../util/constants';
import { HttpErrorResponse } from '@angular/common/http';
import { UserPreferenceService } from '../../../../../services/user-preferences/user-preference.service';
import { PreferenceResponse, UserPreferencePayload } from '../../../../../models/soap-config.interface';

// Section ids used to prefix indices sent to the save API (A:0, B:3)
const SECTION_A = 'classification';
const SECTION_B = 'service';
const INDEX_SEP = ':';

@Component({
    standalone: false,
    selector: 'cva-consultation-acc-eligibility',
    templateUrl: './consultation-acc-eligibility.component.html',
    styleUrl: './consultation-acc-eligibility.component.scss'
})
export class ConsultationAccEligibilityComponent implements OnInit, OnDestroy {
    @ViewChild('doctorDialog') doctorDialog!: TemplateRef<unknown>;
    @Input() patientId: string;
    @Input() practitionerId: string;
    @Input() patientAge: string = ''

    recipients: PractitionerResponse[] = [];
    selectedRecipient: string = '';
    accElgibility: IAccEligibility[] = [];
    accServiceCodes: IAccServiceCode[] = [];
    userPreference: UserPreferencePayload;
    userPreferenceId: string;

    isLoading = false;
    isFetchingRecipients = false;

    cvaUI_url = environment.cvaUI_url;
    expandedRecords = new Set<number>();
    expandedServiceRecords = new Set<number>();
    saveRecipientAsDefault = false;

    private dialogRef: MatDialogRef<unknown>;
    private readonly _unsubscribeAll = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private readonly cvaNotificationService: CVANotificationService,
        private readonly accEligibilityService: AccEligibilityService,
        private readonly recommendationService: ConsultationRecommendationService,
        private readonly practitionerService: PractitionerService,
        private readonly userPreferenceService: UserPreferenceService
    ) { }

    ngOnInit(): void {
        if (environment.RECOMMENDATIONS.ACC_ELIGIBILITY) {
            this.listenToGenerateAccEligibility();
            this.getUserPreferences();
        }
        this.accEligibilityService.accElgibility$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(accElgibility => this.accElgibility = accElgibility);
        this.accEligibilityService.accServiceCode$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(items => this.accServiceCodes = items);
    }

    toggleCheckbox(index: number) {
        this.accEligibilityService.toggleItem(index)
    }

    toggleServiceCheckbox(index: number) {
        this.accEligibilityService.toggleServiceItem(index);
    }

    trackByIndex(index: number): number {
        return index;
    }

    get isSubmitDisabled(): boolean {
        return !this.hasRecordsToPost;
    }

    // true if any item in either section is checked and not yet saved
    private get hasRecordsToPost(): boolean {
        return [...this.accElgibility, ...this.accServiceCodes]
            .some(item => item.checked && !item.isSaved);
    }

    postChecked() {
        if (!this.hasRecordsToPost) return;
        this.fetchRecipients()
    }

    openDoctorDialog() {
        this.saveRecipientAsDefault = false;
        this.selectedRecipient = this.userPreference?.acc_eligibility_recipient || '';

        this.dialogRef = this.dialog.open(this.doctorDialog, {
            width: 'calc(100% - 32px)',
            maxWidth: '500px',
            autoFocus: false
        });
    }

    fetchRecipients() {
        //if already have recipients, avoid refetching list
        if (this.recipients.length) {
            this.openDoctorDialog()
            return
        }
        //fetch the recipients list
        this.isFetchingRecipients = true;
        this.practitionerService.geAlltPractitioners()
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this.isFetchingRecipients = false)
            )
            .subscribe(({
                next: (res) => {
                    this.recipients = [...res.response].sort((a, b) => {
                        const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
                        const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();

                        return nameA.localeCompare(nameB, undefined, {
                            sensitivity: 'base'
                        });
                    });
                    this.openDoctorDialog();
                },
                error: () => {
                    this.cvaNotificationService.showNotification(
                        CVA_CONSTANTS.MESSAGE.RECIPIENTS_ERROR,
                        NotificationClassEnum.ERROR
                    );
                }
            }))
    }

    submit(isAutoSubmit = false) {
        if (!this.selectedRecipient) return;
        // single request for both sections; index is prefixed with section id to avoid collisions
        const records: ISaveAccEligibilityMin[] = [
            ...this.accElgibility
                .map((r, i) => ({ r, i }))
                .filter(({ r }) => r.checked && !r.isSaved)
                .map(({ r, i }) => ({
                    Read_code: r.Read_code,
                    FSN_description: r.FSN_description,
                    index: `${SECTION_A}${INDEX_SEP}${i}`
                })),
            ...this.accServiceCodes
                .map((r, i) => ({ r, i }))
                .filter(({ r }) => r.checked && !r.isSaved)
                .map(({ r, i }) => ({
                    Read_code: r.code,
                    FSN_description: r.description,
                    index: `${SECTION_B}${INDEX_SEP}${i}`
                }))
        ];

        if (!records.length) {
            return;
        }

        const payload: ISaveAccEligibility = {
            accElgibility: records,
            patientId: this.patientId,
            practitionerId: this.selectedRecipient
        }

        this.accEligibilityService.saveAccEligibility(payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({
                next: (response: IAccEligibilityResult) => {
                    if (!isAutoSubmit && this.saveRecipientAsDefault) {
                        this.updatePreference()
                    }
                    const success = response?.success ?? [];
                    const error = response?.error ?? [];
                    this.applyResult(success, true);
                    this.applyResult(error, false);

                    if (error.length === 0) {
                        this.cvaNotificationService.showNotification(
                            CVA_CONSTANTS.MESSAGE.ACC_ELIGIBILITY_ADDED_SUCCESSFULLY,
                            NotificationClassEnum.SUCCESS
                        );
                    } else {
                        this.cvaNotificationService.showNotification(
                            `${error.length} ${CVA_CONSTANTS.MESSAGE.ACC_ELIGIBILITY_ADDED_ERR}`,
                            NotificationClassEnum.ERROR
                        );
                    }
                    this.closeDialog();
                    // persist both sections
                    this.accEligibilityService.setAccEligibility(JSON.parse(JSON.stringify(this.accElgibility)));
                    this.accEligibilityService.setAccServiceCode(JSON.parse(JSON.stringify(this.accServiceCodes)));
                },
                error: (errorResponse: HttpErrorResponse) => {
                    let errorMessage: string = '';
                    if (errorResponse.error?.error) {
                        errorMessage = errorResponse.error.error.errorMessage;
                    }
                    this.cvaNotificationService.showNotification(
                        errorMessage || CVA_CONSTANTS.MESSAGE.save_err_soap_notes,
                        NotificationClassEnum.ERROR
                    );
                }
            }));
    }

    // maps "A:0" / "B:2" back to the right section item; ignores unknown/malformed ids
    private applyResult(ids: Array<number | string>, saved: boolean) {
        ids.forEach(id => {
            const [section, idx] = String(id).split(INDEX_SEP);
            const list: { checked?: boolean; isSaved?: boolean }[] =
                section === SECTION_B ? this.accServiceCodes : this.accElgibility;
            const item = list[Number(idx)];
            if (!item) return;
            if (saved) item.isSaved = true;
            item.checked = false;
        });
    }

    getUserPreferences() {
        this.userPreferenceService.getSoapSettings(this.practitionerId, CVA_CONSTANTS.CONSTS.user_preference)
            .pipe(
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((res: PreferenceResponse) => {
                this.userPreferenceId = res.uid;
                this.userPreference = res?.preference as UserPreferencePayload || {};
                this.selectedRecipient = this.userPreference?.acc_eligibility_recipient || '';
            })
    }

    listenToGenerateAccEligibility() {
        this.recommendationService.generateNotesTrigger$
            .pipe(
                takeUntil(this._unsubscribeAll),
                // Get the current failed state when the trigger happens
                switchMap((payload) =>
                    this.recommendationService.generateNotesFailed$.pipe(
                        take(1),
                        map((failed) => ({ failed, payload }))
                    )
                ),
                // Skip API call if parent already failed
                filter(({ failed }) => !failed),
                filter(({ payload }) =>
                    payload.isTranscriptionChanged ||
                    (this.accElgibility.length === 0 && this.accServiceCodes.length === 0)
                ),
                // Proceed with the API call
                switchMap(({ payload }) => {
                    this.isLoading = true;
                    // both APIs run in parallel; each has its own catchError so one failure doesn't block the other
                    return forkJoin([
                        this.accEligibilityService.generateAccEligibility(payload.transcript)
                            .pipe(
                                catchError((err) => {
                                    console.error('[Child] generateAccEligibility() failed', err);
                                    this.accEligibilityService.setAccEligibility([], true);
                                    return of({ response: [] as IAccEligibility[] })
                                })
                            ),
                        this.accEligibilityService.generateAccServiceCode(payload.transcript, payload.speechServiceTime, this.patientAge)
                            .pipe(
                                catchError((err) => {
                                    console.error('[Child] generateAccServiceCode() failed', err);
                                    this.accEligibilityService.setAccServiceCode([]);
                                    return of({ response: [] as IAccServiceCode[] })
                                })
                            )
                    ]).pipe(
                        finalize(() => {
                            this.isLoading = false;   // always runs (success, error, cancel)
                        })
                    );
                }),
                // Wait until parent signals it's ready before using the response
                switchMap((res) =>
                    combineLatest([
                        this.recommendationService.generateNotesReady$,
                        this.recommendationService.generateNotesFailed$
                    ]).pipe(
                        filter(([ready, failed]) => ready || failed),
                        take(1),
                        map(([ready]) => ready ? res : [{ response: [] }, { response: [] }] as typeof res)
                    )
                )
            )
            .subscribe({
                next: ([accRes, serviceRes]) => {
                    this.isLoading = false;
                    const acc = accRes?.response ?? [];
                    const service = serviceRes?.response ?? [];
                    this.accEligibilityService.setAccEligibility(acc, true);
                    // open drawer from here only if section A didn't already
                    this.accEligibilityService.setAccServiceCode(service, !acc.length);
                }
            });
    }

    updatePreference() {
        const obj: {
            preference_type: string;
            preference: UserPreferencePayload;
            referenceId: string;
        } = {
            preference_type: CVA_CONSTANTS.CONSTS.user_preference,
            preference: { ...this.userPreference, acc_eligibility_recipient: this.selectedRecipient },
            referenceId: this.practitionerId
        };

        if (this.userPreferenceId) {
            this.userPreferenceService.updateSoapSetting(this.userPreferenceId, obj)
                .pipe(
                    takeUntil(this._unsubscribeAll)
                )
                .subscribe((res: { response: PreferenceResponse }) => {
                    this.userPreference = res.response.preference as UserPreferencePayload;
                });
        } else {
            this.userPreferenceService.saveSoapSettings(obj)
                .pipe(
                    takeUntil(this._unsubscribeAll)
                )
                .subscribe((res: { response: PreferenceResponse }) => {
                    this.userPreferenceId = res.response.uid;
                    this.userPreference = res.response.preference as UserPreferencePayload;
                });
        }
    }


    toggleExpanded(index: number): void {
        if (this.expandedRecords.has(index)) {
            this.expandedRecords.delete(index);
        } else {
            this.expandedRecords.add(index);
        }
    }

    toggleServiceExpanded(index: number): void {
        if (this.expandedServiceRecords.has(index)) {
            this.expandedServiceRecords.delete(index);
        } else {
            this.expandedServiceRecords.add(index);
        }
    }

    closeDialog() {
        this.dialogRef?.close();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}

